<template>
  <div>
    <exo-card-form label="Settings">
      <v-checkbox
        v-for="key in standardSettingsKeysSorted"
        :key="key"
        v-model="userSettings.standardConfigurations[key]"
        :label="key"
        class="mt-0 mb-0"
        v-on:change="notifyUpdate()"
      ></v-checkbox>
    </exo-card-form>
    <exo-card-form label="Enable advanced configurations">
      <exo-form-alert type="primary" v-if="disableAdvancedSettings">
        Please upgrade your account to access advanced settings
      </exo-form-alert>
      <v-checkbox
        :disabled="disableAdvancedSettings"
        v-model="selectAll"
        label="Select all"
        :color="selectAllColor"
        class="mt-0 mb-0"
        v-on:change="selectAllAction()"
      ></v-checkbox>

      <v-checkbox
        :disabled="disableAdvancedSettings"
        v-for="key in advancedSettingsKeysSorted"
        :key="key"
        v-model="userSettings.advancedConfigurations[key]"
        :label="advancedConfigurationLabels[key]"
        class="mt-0 mb-0"
        v-on:change="updateSelectAllAndNotifyUpdate()"
      ></v-checkbox>
    </exo-card-form>

    <exo-card-form label="Activated options">
      <v-checkbox
        v-for="option in allOptions"
        :key="option"
        v-model="userOptions"
        :label="option2label(option)"
        :value="option"
        disabled
        class="mt-0 mb-0"
      ></v-checkbox>
    </exo-card-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import errorMixin from '../mixins/logHandler'
import Vue from 'vue'
import fieldsDescription from '../tools/fieldsDescription.json'
import _ from 'lodash'

export default {
  name: 'exo-user-settings',
  mixins: [errorMixin],
  props: {
    disableAdvancedSettings: Boolean
  },

  data() {
    return {
      allOptions: [],
      userOptions: [],
      selectAll: false,
      selectAllColor: 'primary',
      userSettings: {
        standardConfigurations: this.defaultStandardConfigurations,
        advancedConfigurations: this.defaultAdvancedConfigurations
      },
      advancedConfigurationLabels: {}
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      defaultUserSettings: 'defaultUserSettings',
      localDefaultUserSettings: 'localDefaultUserSettings'
    }),

    backgroundStarStyle() {
      return 'background: #3BE287'
    },

    advancedSettingsKeysSorted() {
      if (
        this.userSettings != null &&
        this.userSettings.advancedConfigurations != null
      ) {
        return Object.keys(this.userSettings.advancedConfigurations).sort()
      } else {
        return []
      }
    },

    standardSettingsKeysSorted() {
      if (
        this.userSettings != null &&
        this.userSettings.standardConfigurations != null
      ) {
        // spread defaultStandardConfiguration to avoid users data migration
        return Object.keys({
          ...this.defaultStandardConfigurations,
          ...this.userSettings.standardConfigurations
        }).sort()
      } else {
        return []
      }
    },
    defaultStandardConfigurations() {
      return (
        this.defaultUserSettings?.standardConfigurations ??
        this.localDefaultUserSettings.standardConfigurations
      )
    },
    defaultAdvancedConfigurations() {
      return (
        this.defaultUserSettings?.advancedConfigurations ??
        this.localDefaultUserSettings.advancedConfigurations
      )
    }
  },

  mounted() {
    this.$root.$on(
      'user-settings-data-loaded',
      this.userSettingsDataLoadedHandler
    )
    this.$root.$on(
      'user-options-data-loaded',
      this.userOptionsDataLoadedHandler
    )
    this.loadDefaultSettings().then(() => this.loadUserSettingsData())
    this.updateOptionsList()
  },

  beforeDestroy() {
    this.$root.$off(
      'user-settings-data-loaded',
      this.userSettingsDataLoadedHandler
    )
    this.$root.$off(
      'user-options-data-loaded',
      this.userOptionsDataLoadedHandler
    )
  },

  methods: {
    ...mapActions(['loadDefaultSettings']),
    userSettingsDataLoadedHandler() {
      this.loadUserSettingsData()
    },
    userOptionsDataLoadedHandler() {
      this.updateOptionsList()
    },

    async updateOptionsList() {
      let self = this
      this.$http
        .get('/users/publicOptions')
        .then((response) => {
          self.allOptions = response.data
        })
        .catch((e) => {})
      this.userOptions = this.currentUser.options
    },

    option2label(option) {
      return _.capitalize(option.replace('_', ' '))
    },

    selectAllAction() {
      this.selectAllColor = 'primary'
      for (let setting in this.userSettings.advancedConfigurations) {
        this.userSettings.advancedConfigurations[setting] = this.selectAll
      }
      this.notifyUpdate()
    },

    updateSelectAll() {
      let containsFalse = false
      let containsTrue = false
      for (let setting in this.userSettings.advancedConfigurations) {
        if (this.userSettings.advancedConfigurations[setting]) {
          containsTrue = true
        } else {
          containsFalse = true
        }
      }
      this.selectAll = containsTrue
      this.selectAllColor = containsTrue && containsFalse ? 'grey' : 'primary'
    },

    updateSelectAllAndNotifyUpdate() {
      this.updateSelectAll()
      this.notifyUpdate()
    },

    async notifyUpdate() {
      await this.$http
        .put(`/users/${this.currentUser.id}/usersettings`, this.userSettings)
        .then(() =>
          this.$store.dispatch('updateUserSettings', this.userSettings)
        )
        .catch((e) => {
          this.displayMessage(
            'Network error while updating user settings.',
            e,
            'notification',
            'error'
          )
        })
      this.$root.$emit('user-settings-data-loaded')
    },

    loadUserSettingsData() {
      // load settings or use default
      if (
        this.currentUser.userSettings != null &&
        this.currentUser.userSettings !== ''
      ) {
        this.userSettings = this.currentUser.userSettings
        if (this.currentUser.userSettings.advancedConfigurations == null) {
          Vue.set(
            this.userSettings,
            'advancedConfigurations',
            this.defaultAdvancedConfigurations
          )
        }
        if (
          this.currentUser.userSettings.standardConfigurations == null ||
          this.currentUser.userSettings.standardConfigurations['Dark mode'] ==
            null ||
          this.currentUser.userSettings.standardConfigurations[
            'Receive an email when a mission is shared in a group'
          ] == null ||
          this.currentUser.userSettings.standardConfigurations[
            'Cesium offline'
          ] == null
        ) {
          Vue.set(
            this.userSettings,
            'standardConfigurations',
            this.defaultStandardConfigurations
          )
        }
      } else {
        this.userSettings = {
          advancedConfigurations: this.defaultAdvancedConfigurations,
          standardConfigurations: this.defaultStandardConfigurations
        }
      }

      // then fill advanced properties
      let propertiesToDisplay = []
      for (let type of Object.keys(fieldsDescription)) {
        const mainTypeDef = fieldsDescription[type]
        for (let propertyKey of Object.keys(mainTypeDef.properties)) {
          const propertyDef = mainTypeDef.properties[propertyKey]
          if (propertyDef.advancedConfiguration === true) {
            let key = type + '/' + propertyKey
            propertiesToDisplay.push(key)
            this.advancedConfigurationLabels[key] =
              mainTypeDef.label + ' / ' + propertyDef.label
            if (this.userSettings.advancedConfigurations[key] == null) {
              Vue.set(this.userSettings.advancedConfigurations, key, false)
            }
          }
        }
      }
      // remove obsolete fields
      for (let setting in this.userSettings.advancedConfigurations) {
        if (!propertiesToDisplay.includes(setting)) {
          delete this.userSettings.advancedConfigurations[setting]
        }
      }

      this.updateSelectAll()
    }
  }
}
</script>
